import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type BaseProps } from 'src/modules/shared/types/component.type';
import { type BaseItemDragData } from 'src/modules/shared/types/dnd.types';
import { ReactNode, ComponentType, CSSProperties } from 'react';

interface DragHandleProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  className?: string;
}

// Type for the props passed to children function
export type DraggableChildrenProps = {
  isDragging: boolean;
  DragHandle: ComponentType<DragHandleProps>;
};

interface DraggableItemProps<T> extends Omit<BaseProps, 'children'> {
  id: string;
  data: T;
  disabled?: boolean;
  shouldAnimate?: boolean;
  /** When true, do not render the default white rounded shadow surface. */
  unstyled?: boolean;
  children: ((props: DraggableChildrenProps) => ReactNode) | ReactNode;
}

export const DraggableItem = <T,>({
  children,
  id,
  data,
  disabled = false,
  shouldAnimate = true,
  unstyled = false,
  className = '',
}: DraggableItemProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled,
    data: {
      type: 'item',
      data,
    } satisfies BaseItemDragData<T>,
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: shouldAnimate ? transition : 'none',
  };

  const usesHandle = typeof children === 'function';
  let containerCursorClasses = '';
  if (isDragging) {
    containerCursorClasses = 'cursor-grabbing';
  } else if (!usesHandle) {
    containerCursorClasses = disabled ? 'cursor-default' : 'cursor-grab';
  }

  const itemClassName = unstyled
    ? `${containerCursorClasses} ${className}`
    : `rounded-lg transition-all duration-200 shadow-md hover:shadow-lg ${
        isDragging ? 'shadow-lg' : ''
      } ${containerCursorClasses} ${className}`;

  const DragHandle = ({
    className = '',
    children,
    ...props
  }: DragHandleProps) => {
    if (disabled || !setActivatorNodeRef) return null;

    return (
      <div
        ref={setActivatorNodeRef}
        className={className}
        {...listeners}
        {...attributes}
        {...props}
      >
        {children}
      </div>
    );
  };

  if (typeof children === 'function') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`relative ${itemClassName}`}
      >
        {children({
          isDragging,
          DragHandle,
        })}
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={itemClassName}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
};
